import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import type { AuthenticatedUser } from '@fametrue/shared';
import { MOBILE_QUERY, useMediaQuery } from '../lib/useMediaQuery';
import { FamecertSeal } from './FamecertSeal';
import { UserMenu } from './UserMenu';
import { Wordmark } from './Wordmark';

/**
 * Sidebar del riferimento, unica per tutte le sezioni.
 *
 * Dashboard e pannello di amministrazione ne avevano due copie quasi identiche,
 * ed è bastata la prima modifica responsive per farle divergere: le label delle
 * voci sparivano nella dashboard e restavano nel pannello. Qui il markup è uno
 * solo e cambia la sola voce attiva.
 *
 * Sotto i 900 px la sidebar diventa l'header orizzontale (le `@media` del v40):
 * le voci restano a sole icone e in coda compare il menu utente, che in desktop
 * sta invece nella top bar.
 */
export function AppSidebar({
  user,
  active,
}: {
  user: AuthenticatedUser;
  active: 'dashboard' | 'settings';
}): ReactNode {
  const { t } = useTranslation();
  const compactNav = useMediaQuery(MOBILE_QUERY);

  return (
    <aside className="sidebar">
      <div className="sb-logo">
        <div className="sb-wordmark">
          <Wordmark height={22} variant="compact" />
        </div>
      </div>

      <nav className="sb-nav">
        {/* Il riferimento attenua tutta la navigazione in welcome-mode. Qui
            l'attenuazione resta sulla sola sezione Analisi: vedi app.css. */}
        <div className="sb-analysis">
          <div className="sb-section">{t('shell.navAnalysis')}</div>
          <NavItem
            to="/"
            active={active === 'dashboard'}
            label={t('shell.navDashboard')}
            // `data-view` è l'aggancio del riferimento, tenuto per fedeltà.
            dataView="dashboard"
          >
            <rect x="3" y="3" width="7" height="7" rx="1" />
            <rect x="14" y="3" width="7" height="7" rx="1" />
            <rect x="3" y="14" width="7" height="7" rx="1" />
            <rect x="14" y="14" width="7" height="7" rx="1" />
          </NavItem>
        </div>

        {/* Il pannello è riservato all'admin: al cliente la voce non compare
            affatto, invece di comparire e negare l'accesso al click. */}
        {user.role === 'admin' ? (
          <>
            <div className="sb-divider" />
            <div className="sb-section">{t('shell.navManagement')}</div>
            <NavItem to="/admin" active={active === 'settings'} label={t('shell.navSettings')}>
              <circle cx="12" cy="12" r="3" />
              <path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 010 2.83 2 2 0 01-2.83 0l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-4 0v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83-2.83l.06-.06" />
            </NavItem>
          </>
        ) : null}
      </nav>

      {compactNav ? (
        <div className="sb-user">
          <UserMenu user={user} />
        </div>
      ) : null}

      <div className="sb-footer">
        <FamecertSeal />
        <div className="sb-footer-text">{t('shell.footer')}</div>
      </div>
    </aside>
  );
}

/**
 * Voce di navigazione. La voce attiva resta un `<div>` come nel riferimento:
 * farne un link porterebbe la dashboard da `/brand/:slug` a `/`, perdendo il
 * brand selezionato al primo click distratto.
 *
 * La label sta in uno `<span>` perché sotto i 900 px va spenta, e un nodo di
 * testo nudo non è agganciabile dal CSS. Il nome passa allora dal `title`, che
 * è anche il nome accessibile della voce quando lo `<span>` è `display: none`.
 */
function NavItem({
  to,
  active,
  label,
  dataView,
  children,
}: {
  to: string;
  active: boolean;
  label: string;
  dataView?: string;
  children: ReactNode;
}): ReactNode {
  const icon = (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
      {children}
    </svg>
  );
  const text = <span className="sb-item-label">{label}</span>;

  if (active) {
    return (
      <div className="sb-item active" title={label} data-view={dataView}>
        {icon}
        {text}
      </div>
    );
  }

  return (
    <Link to={to} className="sb-item" title={label} data-view={dataView}>
      {icon}
      {text}
    </Link>
  );
}
